import { useState, useCallback, useRef, useEffect, type RefObject } from 'react'
import { Copy, Check, Download, Code2, Box, SlidersHorizontal, X, Wrench, MessageSquare, FolderOpen } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import type { GCodeResponse } from '../../services/types'
import type { Job, MachineProfile, Tool } from '../../types'
import ToolCribPanel from './ToolCribPanel'
import { useWorkspaceEvents } from '../../contexts/WorkspaceEventContext'

// ---- SettingsPanel (moved from CodeEditor) ----

interface SettingsPanelProps {
  machineName: string
  controller: string
  material: string
  onChange: (updates: Partial<Pick<Job, 'material' | 'units'>>, regenerate: boolean) => void
  onClose: () => void
}

function SettingsPanel({ machineName, controller, material, onChange, onClose }: SettingsPanelProps) {
  const { emit } = useWorkspaceEvents()
  const [editing, setEditing] = useState(false)
  const [editValue, setEditValue] = useState(material)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleSave = () => {
    const newMat = editValue.trim()
    if (!newMat || newMat === material) {
      setEditing(false)
      return
    }
    emit({ type: 'material_change', oldMaterial: material, newMaterial: newMat })
    onChange({ material: newMat }, true)
    setEditing(false)
  }

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus()
  }, [editing])

  return (
    <div className="rounded-xl w-72 p-4 space-y-4 border border-cyan-500/20"
      style={{ background: '#0d1526', boxShadow: '0 8px 32px rgba(0,0,0,0.6)' }}
    >
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] text-slate-500 tracking-widest uppercase">Settings</span>
        <button onClick={onClose} className="text-slate-600 hover:text-slate-300 transition-colors">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div>
        <label className="block font-mono text-[10px] text-slate-500 mb-1.5 tracking-widest uppercase">Machine</label>
        <div className="px-3 py-2 rounded-lg border border-cyan-500/10 bg-white/3 font-mono text-xs text-slate-400">
          <span className="text-slate-300">{machineName}</span>
          <span className="text-slate-600 ml-2">· {controller}</span>
        </div>
      </div>
      <div>
        <label className="block font-mono text-[10px] text-slate-500 mb-1.5 tracking-widest uppercase">Material</label>
        {editing ? (
          <form onSubmit={e => { e.preventDefault(); handleSave() }} className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={editValue}
              onChange={e => setEditValue(e.target.value)}
              onBlur={handleSave}
              onKeyDown={e => e.key === 'Escape' && setEditing(false)}
              className="flex-1 bg-white/5 border border-cyan-400/40 rounded-lg px-3 py-1.5 font-mono text-xs text-slate-200 focus:outline-none"
            />
          </form>
        ) : (
          <div
            onClick={() => { setEditValue(material); setEditing(true) }}
            className="group flex items-center justify-between px-3 py-2 rounded-lg border border-cyan-500/10 bg-white/3 cursor-pointer hover:border-cyan-500/30 transition-colors"
          >
            <span className="font-mono text-xs text-slate-200">{material}</span>
            <span className="font-mono text-[9px] text-slate-600 group-hover:text-cyan-400 transition-colors">edit</span>
          </div>
        )}
      </div>
    </div>
  )
}

// ---- WorkspaceToolbar ----

interface WorkspaceToolbarProps {
  gcode: GCodeResponse | null
  isGenerating: boolean
  isPlanningOperations?: boolean
  machine: MachineProfile
  tools: Tool[]
  material: string
  operationType: 'milling' | 'turning'
  onSettingsChange: (updates: Partial<Pick<Job, 'material' | 'units'>>, regenerate: boolean) => void
  onToolsChange: (tools: Tool[]) => void
  chatOpen: boolean
  onToggleChat: () => void
  viewerOpen: boolean
  onToggleViewer: () => void
  drawerOpen: boolean
  onToggleDrawer: () => void
}

export default function WorkspaceToolbar({
  gcode,
  isGenerating,
  isPlanningOperations,
  machine,
  tools,
  material,
  operationType,
  onSettingsChange,
  onToolsChange,
  chatOpen,
  onToggleChat,
  viewerOpen,
  onToggleViewer,
  drawerOpen,
  onToggleDrawer,
}: WorkspaceToolbarProps) {
  const [copied, setCopied] = useState(false)
  const [openPanel, setOpenPanel] = useState<'settings' | 'toolcrib' | null>(null)
  const panelRef = useRef<HTMLDivElement>(null) as RefObject<HTMLDivElement>
  const toolbarRef = useRef<HTMLDivElement>(null) as RefObject<HTMLDivElement>

  const lineCount = gcode ? gcode.code.split('\n').length : 0

  // Close panel on click outside
  useEffect(() => {
    if (!openPanel) return
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        panelRef.current && !panelRef.current.contains(target) &&
        toolbarRef.current && !toolbarRef.current.contains(target)
      ) {
        setOpenPanel(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [openPanel])

  const toggle = (panel: 'settings' | 'toolcrib') =>
    setOpenPanel(prev => prev === panel ? null : panel)

  const handleCopy = useCallback(async () => {
    if (!gcode) return
    await navigator.clipboard.writeText(gcode.code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [gcode])

  const handleExport = useCallback(async () => {
    if (!gcode) return
    if ('showSaveFilePicker' in window) {
      try {
        const handle = await (window as Window & typeof globalThis & { showSaveFilePicker: (opts: object) => Promise<FileSystemFileHandle> }).showSaveFilePicker({
          suggestedName: 'program.nc',
          types: [{ description: 'NC File', accept: { 'text/plain': ['.nc', '.gcode', '.tap'] } }],
        })
        const writable = await handle.createWritable()
        await writable.write(gcode.code)
        await writable.close()
      } catch (err) {
        if ((err as Error).name !== 'AbortError') throw err
      }
    } else {
      const blob = new Blob([gcode.code], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'program.nc'
      a.click()
      URL.revokeObjectURL(url)
    }
  }, [gcode])

  const panelBtnClass = (panel: 'settings' | 'toolcrib') => `
    flex items-center gap-1.5 px-3 py-1.5 rounded-lg whitespace-nowrap
    font-mono text-xs border transition-all duration-150
    ${openPanel === panel
      ? 'border-cyan-400/50 text-cyan-300 bg-cyan-500/8'
      : 'border-cyan-500/25 text-slate-400 hover:border-cyan-400/50 hover:text-cyan-300 hover:bg-cyan-500/8'
    }
  `

  const actionBtnClass = (disabled?: boolean) => `
    flex items-center gap-1.5 px-3 py-1.5 rounded-lg whitespace-nowrap
    font-mono text-xs border transition-all duration-150
    border-cyan-500/25 text-slate-400
    hover:border-cyan-400/50 hover:text-cyan-300 hover:bg-cyan-500/8
    ${disabled ? 'opacity-30 cursor-not-allowed' : ''}
  `

  return (
    <div className="relative z-40 flex items-center h-12 px-4 border-b border-cyan-500/15 bg-[#030712]/60 backdrop-blur-sm flex-shrink-0">
      {/* Projects drawer toggle */}
      <button
        onClick={onToggleDrawer}
        className={`
          flex items-center justify-center w-8 h-8 rounded-lg mr-1.5 flex-shrink-0
          border transition-all duration-150
          ${drawerOpen
            ? 'border-cyan-400/50 text-cyan-300 bg-cyan-500/10'
            : 'border-cyan-500/20 text-slate-500 hover:border-cyan-400/40 hover:text-cyan-300 hover:bg-cyan-500/8'
          }
        `}
        title="Projects"
      >
        <FolderOpen className="w-3.5 h-3.5" />
      </button>

      {/* Chat toggle */}
      <button
        onClick={onToggleChat}
        className={`
          flex items-center justify-center w-8 h-8 rounded-lg mr-3 flex-shrink-0
          border transition-all duration-150
          ${chatOpen
            ? 'border-cyan-400/50 text-cyan-300 bg-cyan-500/10'
            : 'border-cyan-500/20 text-slate-500 hover:border-cyan-400/40 hover:text-cyan-300 hover:bg-cyan-500/8'
          }
        `}
        title={chatOpen ? 'Hide chat (⌘B)' : 'Show chat (⌘B)'}
      >
        <MessageSquare className="w-3.5 h-3.5" />
      </button>

      {/* G-CODE stats + buttons */}
      <div ref={toolbarRef} className="flex items-center gap-3 flex-1 min-w-0 relative">
        {/* G-CODE label + stats */}
        <Code2 className="w-4 h-4 text-cyan-400 flex-shrink-0" />
        <span className="font-mono text-xs font-semibold tracking-widest uppercase text-cyan-400 flex-shrink-0">
          G-CODE
        </span>
        {gcode && (
          <span className="font-mono text-[10px] text-slate-600 flex-shrink-0">
            {lineCount} lines · {gcode.estimatedTime} est.
          </span>
        )}
        {isGenerating && gcode && (
          <span className="flex items-center gap-1.5 flex-shrink-0">
            <motion.span
              className="inline-block w-1.5 h-1.5 rounded-full bg-cyan-400"
              animate={{ opacity: [1, 0.3, 1] }}
              transition={{ duration: 1, repeat: Infinity }}
            />
            <span className="font-mono text-[10px] text-cyan-400/70 tracking-widest uppercase">
              Streaming…
            </span>
          </span>
        )}
        {isPlanningOperations && (
          <span className="flex items-center gap-1.5 flex-shrink-0">
            <motion.span
              className="inline-block w-1.5 h-1.5 rounded-full bg-cyan-400"
              animate={{ opacity: [1, 0.3, 1] }}
              transition={{ duration: 1, repeat: Infinity }}
            />
            <span className="font-mono text-[10px] text-cyan-400/70 tracking-widest uppercase">
              Planning…
            </span>
          </span>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Action buttons */}
        <button
          onClick={onToggleViewer}
          className={`
            flex items-center gap-1.5 px-3 py-1.5 rounded-lg whitespace-nowrap
            font-mono text-xs border transition-all duration-150
            ${viewerOpen
              ? 'border-cyan-400/50 text-cyan-300 bg-cyan-500/8'
              : 'border-cyan-500/25 text-slate-400 hover:border-cyan-400/50 hover:text-cyan-300 hover:bg-cyan-500/8'
            }
          `}
          title="View Toolpath (⌘3)"
          data-tour="viewer-btn"
        >
          <Box className="w-3 h-3" /> View Toolpath
        </button>
        <button data-tour="tool-crib" onClick={() => toggle('toolcrib')} className={panelBtnClass('toolcrib')}>
          <Wrench className="w-3 h-3" /> Tool Crib
        </button>
        <button data-tour="settings" onClick={() => toggle('settings')} className={panelBtnClass('settings')}>
          <SlidersHorizontal className="w-3 h-3" /> Settings
        </button>

        {/* Copy */}
        <button onClick={handleCopy} disabled={!gcode} className={actionBtnClass(!gcode)}>
          <AnimatePresence mode="wait" initial={false}>
            {copied ? (
              <motion.span key="check" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.8, opacity: 0 }} className="flex items-center gap-1.5 text-green-400">
                <Check className="w-3 h-3" /> Copied
              </motion.span>
            ) : (
              <motion.span key="copy" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.8, opacity: 0 }} className="flex items-center gap-1.5">
                <Copy className="w-3 h-3" /> Copy
              </motion.span>
            )}
          </AnimatePresence>
        </button>

        {/* Export */}
        <button onClick={handleExport} disabled={!gcode} className={actionBtnClass(!gcode)}>
          <Download className="w-3 h-3" /> Export .nc
        </button>

        {/* Popover panels */}
        <AnimatePresence>
          {openPanel && (
            <motion.div
              ref={panelRef}
              key={openPanel}
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.15 }}
              className="absolute top-full right-0 mt-2 z-30"
            >
              {openPanel === 'toolcrib' && (
                <ToolCribPanel
                  tools={tools}
                  operationType={operationType}
                  onToolsChange={onToolsChange}
                  onClose={() => setOpenPanel(null)}
                />
              )}
              {openPanel === 'settings' && (
                <SettingsPanel
                  machineName={machine.name}
                  controller={machine.controller}
                  material={material}
                  onChange={onSettingsChange}
                  onClose={() => setOpenPanel(null)}
                />
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}
